import { useState, useCallback } from 'react';
import logger from '../utils/logger';

/**
 * Custom hook for managing UI state in a consolidated way
 * This reduces the number of useState calls and provides better organization
 */
export const useUIState = () => {
    // Navigation state
    const [navigationState, setNavigationState] = useState({
        page: 'dashboard',
        pageHistory: ['dashboard'], // Start with dashboard, not loading
        activeModule: null,
        returnToUnit: null,
        expandedPathUnits: [1],
    });

    // User interaction state
    const [interactionState, setInteractionState] = useState({
        feedback: { message: '', type: '' },
        reward: null,
        hasUserInteracted: false,
        isSessionMuted: false,
    });

    // Onboarding state
    const [onboardingState, setOnboardingState] = useState({
        step: 0,
        data: { language: null },
        authAction: 'signup',
    });

    // Parental gate state
    const [gateState, setGateState] = useState({
        isOpen: false,
        successAction: null,
    });

    // Navigation control functions
    const setPage = useCallback((page) => {
        setNavigationState(prev => {
            if (prev.page !== page) {
                // Filter out invalid pages that shouldn't be in history
                // Note: 'loading' and 'onboarding' are intentionally excluded from valid pages
                const validPages = [
                    'dashboard', 'learning_path', 'practice', 'game_zone', 'sticker_book', 
                    'storybook', 'profile', 'settings', 'parental_auth',
                    'lesson', 'quiz', 'quiz_results', 'profile_picker', 'add_new_profile',
                    'forgot_password', 'support_page', 'subscription_page', 'post_subscribe_onboarding',
                    'trial_complete_transition', 'notifications', 'unsubscribe', 'analytics_dashboard',
                    'modal_test', 'categorySort_activity', 'colorMatch_activity', 'listenFind_activity',
                    'numberMatch_activity', 'sentenceScramble_activity', 'whatAmI_activity',
                    'simpleConversation_activity'
                ];
                
                // Only add to history if it's a valid page
                const shouldAddToHistory = validPages.includes(page) && prev.pageHistory[prev.pageHistory.length - 1] !== page;
                
                logger.info('useUIState', 'Info', page, 'valid:', validPages.includes(page), 'shouldAddToHistory:', shouldAddToHistory);
                
                // Update browser history to support native back gesture on mobile
                // This allows Android/iOS back gestures to work with in-app navigation
                if (shouldAddToHistory && typeof window !== 'undefined') {
                    const currentUrl = window.location.href;
                    const newUrl = `${window.location.origin}${window.location.pathname}#${page}`;
                    
                    // Only push if the URL is actually changing
                    if (currentUrl !== newUrl) {
                        logger.info('useUIState', 'Info', page);
                        window.history.pushState({ page: page }, '', newUrl);
                    }
                }
                
                return {
                    ...prev,
                    page,
                    pageHistory: shouldAddToHistory 
                        ? [...prev.pageHistory, page].slice(-50) // Limit history size
                        : prev.pageHistory
                };
            }
            return prev;
        });
    }, []);

    const goBack = useCallback(() => {
        setNavigationState(prev => {
            logger.info('useUIState', 'Info', prev.pageHistory);
            if (prev.pageHistory.length > 1) {
                const newHistory = prev.pageHistory.slice(0, -1);
                const previousPage = newHistory[newHistory.length - 1];
                logger.info('useUIState', 'Info', previousPage, 'newHistory:', newHistory);
                
                // Filter out invalid pages that shouldn't be in history
                // Note: 'loading' and 'onboarding' are intentionally excluded from valid pages
                const validPages = [
                    'dashboard', 'learning_path', 'practice', 'game_zone', 'sticker_book', 
                    'storybook', 'profile', 'settings', 'parental_auth',
                    'lesson', 'quiz', 'quiz_results', 'profile_picker', 'add_new_profile',
                    'forgot_password', 'support_page', 'subscription_page', 'post_subscribe_onboarding',
                    'trial_complete_transition', 'notifications', 'unsubscribe', 'analytics_dashboard',
                    'modal_test', 'categorySort_activity', 'colorMatch_activity', 'listenFind_activity',
                    'numberMatch_activity', 'sentenceScramble_activity', 'whatAmI_activity',
                    'simpleConversation_activity'
                ];
                
                // If the previous page is invalid, try to find a valid one
                if (!validPages.includes(previousPage)) {
                    logger.info('useUIState', 'Info');
                    const validPreviousPage = newHistory.reverse().find(page => validPages.includes(page));
                    if (validPreviousPage) {
                        logger.info('useUIState', 'Info', validPreviousPage);
                        return {
                            ...prev,
                            page: validPreviousPage,
                            pageHistory: [validPreviousPage] // Reset history to just this page
                        };
                    } else {
                        // Smart fallback based on current page context
                        let fallbackPage = 'learning_path';
                        if (prev.page === 'lesson' || prev.page === 'quiz' || prev.page === 'quiz_results') {
                            fallbackPage = 'learning_path';
                        } else if (prev.page === 'practice') {
                            fallbackPage = 'learning_path';
                        } else if (prev.page === 'game_zone' || prev.page === 'sticker_book' || prev.page === 'storybook') {
                            fallbackPage = 'dashboard';
                        }
                        
                        logger.info('useUIState', 'Info', fallbackPage);
                        return {
                            ...prev,
                            page: fallbackPage,
                            pageHistory: [fallbackPage]
                        };
                    }
                }
                
                return {
                    ...prev,
                    page: previousPage,
                    pageHistory: newHistory
                };
            }
            return prev;
        });
    }, []);

    const setActiveModule = useCallback((module) => {
        setNavigationState(prev => ({ ...prev, activeModule: module }));
    }, []);

    const setReturnToUnit = useCallback((unit) => {
        setNavigationState(prev => ({ ...prev, returnToUnit: unit }));
    }, []);

    const setExpandedPathUnits = useCallback((units) => {
        setNavigationState(prev => ({ ...prev, expandedPathUnits: units }));
    }, []);


    // Interaction control functions
    const showFeedback = useCallback((message, type) => {
        setInteractionState(prev => ({ ...prev, feedback: { message, type } }));
    }, []);

    const setReward = useCallback((reward) => {
        setInteractionState(prev => ({ ...prev, reward }));
    }, []);

    const setHasUserInteracted = useCallback((hasInteracted) => {
        setInteractionState(prev => ({ ...prev, hasUserInteracted: hasInteracted }));
    }, []);

    const setIsSessionMuted = useCallback((isMuted) => {
        setInteractionState(prev => ({ ...prev, isSessionMuted: isMuted }));
    }, []);

    // Onboarding control functions
    const setOnboardingStep = useCallback((step) => {
        setOnboardingState(prev => ({ ...prev, step }));
    }, []);

    const setOnboardingData = useCallback((data) => {
        setOnboardingState(prev => ({ ...prev, data }));
    }, []);

    const setAuthAction = useCallback((action) => {
        setOnboardingState(prev => ({ ...prev, authAction: action }));
    }, []);

    // Gate control functions
    const setIsGateOpen = useCallback((isOpen) => {
        setGateState(prev => ({ ...prev, isOpen }));
    }, []);

    const setGateSuccessAction = useCallback((action) => {
        setGateState(prev => ({ ...prev, successAction: action }));
    }, []);

    // Debug: Check what we're returning
    logger.info('useUIState', 'Info', typeof navigationState.page, 'value:', navigationState.page);
    
    return {
        // Navigation state
        page: navigationState.page,
        pageHistory: navigationState.pageHistory,
        activeModule: navigationState.activeModule,
        returnToUnit: navigationState.returnToUnit,
        expandedPathUnits: navigationState.expandedPathUnits,

        // Interaction state
        feedback: interactionState.feedback,
        reward: interactionState.reward,
        hasUserInteracted: interactionState.hasUserInteracted,
        isSessionMuted: interactionState.isSessionMuted,

        // Onboarding state
        onboardingStep: onboardingState.step,
        onboardingData: onboardingState.data,
        authAction: onboardingState.authAction,

        // Gate state
        isGateOpen: gateState.isOpen,
        gateSuccessAction: gateState.successAction,

        // Navigation control functions
        setPage,
        goBack,
        setActiveModule,
        setReturnToUnit,
        setExpandedPathUnits,

        // Interaction control functions
        showFeedback,
        setReward,
        setHasUserInteracted,
        setIsSessionMuted,

        // Onboarding control functions
        setOnboardingStep,
        setOnboardingData,
        setAuthAction,

        // Gate control functions
        setIsGateOpen,
        setGateSuccessAction,
    };
};

